import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../components/ui/button'
import { SOCIAL_LINKEDIN } from '../../config';
import { ChevronLeft, ChevronRight, Mail, Search } from 'lucide-react'
import googleLogo from '../../pages/authentication/google-logo.svg'
import LinkedInIcon from '../../icons/linkedin.svg'
import { languagePrefixes } from '../../i18n'

const CAROUSEL_IMAGES = [
  'https://crino-cdn.nyc3.cdn.digitaloceanspaces.com/pages-images/homepage/features-carousel/crino-sample-1.webp',
  'https://crino-cdn.nyc3.cdn.digitaloceanspaces.com/pages-images/homepage/features-carousel/crino-sample-2.webp',
  'https://crino-cdn.nyc3.cdn.digitaloceanspaces.com/pages-images/homepage/features-carousel/crino-sample-3.webp',
  'https://crino-cdn.nyc3.cdn.digitaloceanspaces.com/pages-images/homepage/features-carousel/crino-sample-4.webp',
  'https://crino-cdn.nyc3.cdn.digitaloceanspaces.com/pages-images/homepage/features-carousel/crino-sample-5.webp',
  'https://crino-cdn.nyc3.cdn.digitaloceanspaces.com/pages-images/homepage/features-carousel/crino-sample-6.webp',
]

const MainHeader = () => {
  const { t, i18n } = useTranslation()
  const [activeSlide, setActiveSlide] = useState(0)

  const langPrefix = useMemo(() => {
    const code = (i18n?.language || 'en').split('-')[0]
    const p = languagePrefixes[code] || ''
    return p === '' || p.startsWith('/') ? p : `/${p}`
  }, [i18n?.language])

  const withLang = (path) => `${langPrefix}${path.startsWith('/') ? path : `/${path}`}`

  const slides = useMemo(() => CAROUSEL_IMAGES.map((src, index) => {
    const key = `pageHome.content.heroCarousel.slides.${index + 1}`

    return {
      src,
      title: t(`${key}.title`),
      text: t(`${key}.text`),
      alt: t(`${key}.alt`),
    }
  }), [t])

  const goToPreviousSlide = () => {
    setActiveSlide((current) => (current === 0 ? slides.length - 1 : current - 1))
  }

  const goToNextSlide = () => {
    setActiveSlide((current) => (current === slides.length - 1 ? 0 : current + 1))
  }

  const currentSlide = slides[activeSlide]

  return (
    <section className="relative py-16 sm:py-12 left-1/2 -translate-x-1/2 w-screen flex flex-col lg:flex-row bg-gradient-to-r from-slate-50 to-gray-100">
      <div className="w-[80%] max-w-screen-xl mx-auto flex flex-col lg:flex-row gap-12 lg:gap-24 items-center">
        {/* Left: text */}
        <div className="w-full lg:w-2/5 text-center py-6 sm:py-20 lg:py-0">
          <h1 className="!text-3xl sm:!text-4xl mb-8">{t('pageHome.content.t30')}</h1>
          <p className="text-xl ">{t('pageHome.content.t31')}</p>

          <div className="flex flex-col items-center mt-10 space-y-3">
            <Button
              href={withLang('/find-investors?utm_source=crino-homepage&utm_medium=find-the-right-quick-overview-button')}
              variant="outline" size="lg" iconLeft={<Search className="h-4 w-4" />} className="w-full max-w-sm"
            >
              {t('pageHome.content.t3', { defaultValue: 'Find the right investors' })}
            </Button>

            <Button
              href={withLang('/signup?utm_source=crino-homepage&utm_medium=find-the-right-investor-signup-button')}
              variant="default" size="lg"
              iconLeft={<><Mail className="h-4 w-4 mr-2" /><img src={googleLogo} alt="Google" className="h-4 w-4" /></>}
              className="w-full max-w-sm"
            >
              {t('pageHome.content.t4', { defaultValue: 'Create a free account' })}
            </Button>
            <a
              href={SOCIAL_LINKEDIN}
              target="_blank"
              rel="noopener noreferrer"
              className="my-4 inline-flex items-start lg:items-center justify-center gap-2 text-sm leading-5 text-slate-600 hover:text-slate-900 text-center"
              aria-label={t('pageHome.content.followLinkedIn', {
                defaultValue: 'Follow us on LinkedIn for investor deal news and stats',
              })}
            >
              <img
                src={LinkedInIcon}
                alt="LinkedIn"
                className="w-7 h-7 block shrink-0 mt-[2px] lg:mt-0"
              />
              <span className="underline decoration-dotted underline-offset-4">
                {t('pageHome.content.followLinkedIn', {
                  defaultValue: 'Follow us on LinkedIn for investor deal news and stats',
                })}
              </span>
            </a>
          </div>
        </div>

        <div className="w-full lg:w-3/5">
          <section
            className="rounded-2xl border border-slate-200 bg-white/90 p-3 shadow-xl shadow-slate-200/80"
            aria-label={t('pageHome.content.heroCarousel.label')}
          >
            <div className="mb-3 flex items-center justify-between gap-4 px-1">
              <p className="text-sm font-semibold text-blue-600">{t('pageHome.content.heroCarousel.label')}</p>
              <p className="text-sm text-slate-500">
                {t('pageHome.content.heroCarousel.counter', {
                  current: activeSlide + 1,
                  total: slides.length,
                })}
              </p>
            </div>

            <div className="relative aspect-video overflow-hidden rounded-xl border border-cyan-300 bg-white">
              {slides.map((slide, index) => (
                <figure
                  key={slide.src}
                  className={`absolute inset-0 transition-opacity duration-300 ${index === activeSlide ? 'opacity-100' : 'opacity-0'}`}
                  aria-hidden={index !== activeSlide}
                >
                  <img
                    src={slide.src}
                    alt={slide.alt}
                    className="h-full w-full object-contain"
                    width="1920"
                    height="1080"
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchpriority={index === 0 ? 'high' : 'auto'}
                    decoding="async"
                  />
                  <figcaption className="sr-only">
                    {slide.title}. {slide.text}
                  </figcaption>
                </figure>
              ))}

              <div className="absolute inset-y-0 left-3 flex items-center">
                <button
                  type="button"
                  className="inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-900 shadow-md transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onClick={goToPreviousSlide}
                  aria-label={t('pageHome.content.heroCarousel.previous')}
                >
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <div className="absolute inset-y-0 right-3 flex items-center">
                <button
                  type="button"
                  className="inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-900 shadow-md transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onClick={goToNextSlide}
                  aria-label={t('pageHome.content.heroCarousel.next')}
                >
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="h-[120px] overflow-hidden" aria-live="polite">
                <h2 className="mb-2 text-xl font-semibold text-slate-950">{currentSlide.title}</h2>
                <p className="max-w-xl text-sm leading-6 text-slate-600">{currentSlide.text}</p>
              </div>

              <div className="flex shrink-0 items-center gap-2" aria-label={t('pageHome.content.heroCarousel.pagination')}>
                {slides.map((slide, index) => (
                  <button
                    key={slide.src}
                    type="button"
                    className={`h-2.5 cursor-pointer rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${index === activeSlide ? 'w-8 bg-slate-950' : 'w-2.5 bg-slate-300 hover:bg-slate-500'}`}
                    onClick={() => setActiveSlide(index)}
                    aria-label={t('pageHome.content.heroCarousel.goTo', { number: index + 1 })}
                    aria-current={index === activeSlide ? 'true' : undefined}
                  />
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </section>
  )
}

export default MainHeader
